if (!customElements.get('byte-product-quote-request')) {
  class ByteProductQuoteRequest extends HTMLElement {
  constructor() {
    super();

    this.isQuoteMode = false;
    this.quoteThreshold = 1000;
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);
    this.boundHandleDelegatedQuantityEvent = this.handleDelegatedQuantityEvent.bind(this);
    this.boundHandleVariantChange = this.handleVariantChange.bind(this);
    this.boundHandleEscapeKey = this.handleEscapeKey.bind(this);
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
    this.quantityUpdateUnsubscriber = null;
    this.variantChangeUnsubscriber = null;
    this.quantityObserver = null;
    this.quantityEventRoot = null;
    this.qvlSyncTimeoutId = null;
  }

  connectedCallback() {
    this.#cacheElements();
    this.#bindEvents();
    this.#observeQuantityInput();
    this.#updateQuoteMode();
  }

  disconnectedCallback() {
    this.#unbindEvents();
  }

  handleFormSubmit(event) {
    event.preventDefault();
    this.#submitQuoteRequest(event);
  }

  handleClick(event) {
    const actionTrigger = event.target.closest('[data-quote-action]');
    if (!actionTrigger) return;

    const isInsideComponent = this.contains(actionTrigger);
    const isInsideOverlay = this.overlay?.contains(actionTrigger);
    if (!isInsideComponent && !isInsideOverlay) return;

    const quoteAction = actionTrigger.dataset.quoteAction;

    if (quoteAction === 'open') {
      event.preventDefault();
      this.#openModal();
    }

    if (quoteAction === 'close') {
      event.preventDefault();
      this.#closeModal();
    }

    if (quoteAction === 'submit') {
      event.preventDefault();
      this.#submitQuoteRequest(event);
    }
  }

  handleQuantityChange() {
    this.#cacheQuantityInput();
    this.#updateQuoteMode();
  }

  handleDelegatedQuantityEvent(event) {
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) return;

    const isQvlControl = Boolean(eventTarget.closest('.qvl-quantity__control, .qvl-input-wrapper'));
    const isQuantityInput = eventTarget.matches(
      '.qvl-input-control, .qvl-basic-input, .quantity__input, [name="quantity"], [id^="Quantity-"]'
    );

    if (!isQvlControl && !isQuantityInput) return;

    this.#cacheQuantityInput();
    this.#updateQuoteMode();

    // QVL plus/minus often updates the value after the click event.
    if (event.type === 'click' && isQvlControl) {
      requestAnimationFrame(() => {
        this.#cacheQuantityInput();
        this.#updateQuoteMode();
      });

      clearTimeout(this.qvlSyncTimeoutId);
      this.qvlSyncTimeoutId = setTimeout(() => {
        this.#cacheQuantityInput();
        this.#updateQuoteMode();
      }, 75);
    }
  }

  handleVariantChange(event) {
    if (event.data?.sectionId !== this.sectionId) return;

    this.#cacheElements();
    this.#bindQuantityInputEvents();
    this.#updateQuoteMode();
  }

  handleEscapeKey(event) {
    if (event.key !== 'Escape') return;
    if (this.overlay?.hasAttribute('hidden')) return;

    this.#closeModal();
  }

  #cacheElements() {
    this.sectionId = this.dataset.sectionId;
    this.productInfo = this.closest('product-info');
    this.#cacheQuantityInput();
    this.addToCartButton = this.querySelector('[data-product-submit]');
    this.quoteButton = this.querySelector('[data-quote-action="open"]');
    this.overlay = this.querySelector('[data-quote-panel]');
    this.form = this.querySelector('.byte-quote-request__form');
    this.successMessage = this.querySelector('[data-quote-success]');
    this.errorMessage = this.querySelector('[data-quote-error]');
    this.emailInput = this.querySelector('[data-quote-email]');
    this.bodyInput = this.querySelector('[data-quote-body-input]');
    this.submitButton = this.querySelector('[data-quote-action="submit"]');
    this.quoteThreshold = Number(this.dataset.quoteThreshold) || 1000;
  }

  #getQuantitySearchRoot() {
    return this.productInfo || this.closest('.product') || this.parentElement || document;
  }

  #cacheQuantityInput() {
    const searchRoot = this.#getQuantitySearchRoot();

    // Prefer Quantity Volume / QVL app input when present on PDP.
    this.quantityInput =
      searchRoot.querySelector('.qvl-input-wrapper .qvl-input-control') ||
      searchRoot.querySelector('.qvl-basic-input.qvl-input-control') ||
      searchRoot.querySelector('input.qvl-input-control') ||
      searchRoot.querySelector(`#Quantity-${this.sectionId}`) ||
      searchRoot.querySelector('.quantity__input');
  }

  #bindEvents() {
    this.addEventListener('click', this.boundHandleClick);
    document.addEventListener('click', this.boundHandleClick);
    document.addEventListener('keydown', this.boundHandleEscapeKey);

    this.#bindQuantityInputEvents();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.boundHandleQuantityChange);
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.boundHandleVariantChange);
    }

    this.form?.addEventListener('submit', this.boundHandleFormSubmit);
  }

  #bindQuantityInputEvents() {
    this.#unbindQuantityInputEvents();

    const eventRoot = this.#getQuantitySearchRoot();
    if (!eventRoot || eventRoot === document) return;

    // Capture/delegate so late-injected QVL quantity controls are covered.
    eventRoot.addEventListener('input', this.boundHandleDelegatedQuantityEvent, true);
    eventRoot.addEventListener('change', this.boundHandleDelegatedQuantityEvent, true);
    eventRoot.addEventListener('click', this.boundHandleDelegatedQuantityEvent, true);
    this.quantityEventRoot = eventRoot;
  }

  #unbindQuantityInputEvents() {
    if (!this.quantityEventRoot) return;

    this.quantityEventRoot.removeEventListener('input', this.boundHandleDelegatedQuantityEvent, true);
    this.quantityEventRoot.removeEventListener('change', this.boundHandleDelegatedQuantityEvent, true);
    this.quantityEventRoot.removeEventListener('click', this.boundHandleDelegatedQuantityEvent, true);
    this.quantityEventRoot = null;
  }

  #observeQuantityInput() {
    const observeRoot = this.#getQuantitySearchRoot();
    if (!observeRoot || observeRoot === document) return;

    // QVL injects its quantity UI after load; refresh mode when that markup appears.
    this.quantityObserver = new MutationObserver(() => {
      const previousInput = this.quantityInput;
      this.#cacheQuantityInput();

      if (this.quantityInput === previousInput) return;

      this.#updateQuoteMode();
    });

    this.quantityObserver.observe(observeRoot, {
      childList: true,
      subtree: true,
    });
  }

  #unbindEvents() {
    this.removeEventListener('click', this.boundHandleClick);
    document.removeEventListener('click', this.boundHandleClick);
    document.removeEventListener('keydown', this.boundHandleEscapeKey);

    this.#unbindQuantityInputEvents();
    this.quantityObserver?.disconnect();
    this.quantityObserver = null;
    clearTimeout(this.qvlSyncTimeoutId);

    this.quantityUpdateUnsubscriber?.();
    this.variantChangeUnsubscriber?.();
    this.form?.removeEventListener('submit', this.boundHandleFormSubmit);
  }

  #getQuantity() {
    this.#cacheQuantityInput();
    const quantity = Number(this.quantityInput?.value);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  #updateQuoteMode() {
    const quantity = this.#getQuantity();
    const isQuoteMode = quantity > this.quoteThreshold;

    this.isQuoteMode = isQuoteMode;
    this.classList.toggle('is-quote-mode', isQuoteMode);

    if (this.quoteButton) {
      const isSoldOut = this.addToCartButton?.hasAttribute('disabled');
      this.quoteButton.toggleAttribute('hidden', isSoldOut);
      this.quoteButton.toggleAttribute('disabled', isSoldOut);
    }
  }

  #openModal() {
    if (!this.overlay) return;

    if (!this.overlayPlaceholder) {
      this.overlayPlaceholder = document.createComment('byte-quote-request-overlay');
      this.overlay.parentNode.insertBefore(this.overlayPlaceholder, this.overlay);
    }

    document.body.appendChild(this.overlay);
    this.overlay.classList.add('is-portaled');
    this.overlay.removeAttribute('hidden');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.form?.removeAttribute('hidden');
    this.successMessage?.setAttribute('hidden', '');
    this.errorMessage?.setAttribute('hidden', '');
    this.emailInput?.focus();
    document.body.classList.add('overflow-hidden');
  }

  #closeModal() {
    if (!this.overlay) return;

    this.overlay.setAttribute('hidden', '');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-portaled');

    if (this.overlayPlaceholder?.parentNode) {
      this.overlayPlaceholder.parentNode.insertBefore(this.overlay, this.overlayPlaceholder);
    }

    document.body.classList.remove('overflow-hidden');
    this.quoteButton?.focus();
  }

  async #submitQuoteRequest(event) {
    event?.preventDefault();

    const form = this.form || this.overlay?.querySelector('.byte-quote-request__form');
    if (!form) {
      this.#showError(this.dataset.errorSubmit);
      return;
    }

    const email = this.emailInput?.value?.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#showError(this.dataset.errorInvalidEmail);
      return;
    }

    const quantity = this.#getQuantity();
    const productTitle = this.dataset.productTitle || '';
    const productUrl = this.dataset.productUrl || window.location.href;
    const variantId = this.closest('product-form')?.querySelector('[name="id"]')?.value || this.dataset.variantId;

    const messageBody = [
      'Teklif Talebi',
      '----------------',
      `Ürün: ${productTitle}`,
      `Ürün linki: ${productUrl}`,
      `Varyant ID: ${variantId}`,
      `Talep edilen adet: ${quantity}`,
      `Müşteri e-posta: ${email}`,
    ].join('\n');

    const bodyInput = form.querySelector('[data-quote-body-input]');
    if (bodyInput) {
      bodyInput.value = messageBody;
    }

    this.#setLoadingState(true);
    this.#showError('');

    try {
      const isSubmitted = await this.#submitContactForm(form);
      if (!isSubmitted) {
        throw new Error('Contact form rejected');
      }

      form.setAttribute('hidden', '');
      this.successMessage?.removeAttribute('hidden');
    } catch (error) {
      this.#showError(this.dataset.errorSubmit);
      console.error(error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  #submitContactForm(form) {
    return new Promise((resolve) => {
      const iframeName = `byte-quote-contact-${Date.now()}`;
      const iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none';
      document.body.appendChild(iframe);

      let isSettled = false;

      const settle = (isSuccess) => {
        if (isSettled) return;
        isSettled = true;
        form.removeAttribute('target');
        iframe.removeEventListener('load', handleLoad);
        setTimeout(() => iframe.remove(), 1500);
        resolve(isSuccess);
      };

      const handleLoad = () => {
        try {
          const frameDocument = iframe.contentDocument || iframe.contentWindow?.document;
          const frameHtml = frameDocument?.body?.innerHTML || '';
          const frameUrl = iframe.contentWindow?.location?.href || '';

          const hasError =
            frameHtml.includes('form-status caption-large text-body') ||
            frameHtml.includes('contact__field-error') ||
            frameHtml.includes('error_heading');

          const hasSuccess =
            frameUrl.includes('contact_posted=true') ||
            frameHtml.includes('post_success') ||
            frameHtml.includes('form-status-list form__message') ||
            frameHtml.includes('Bizimle iletişime geçtiğiniz için') ||
            frameHtml.includes('teşekkür ederiz');

          settle(hasSuccess && !hasError);
        } catch {
          settle(true);
        }
      };

      iframe.addEventListener('load', handleLoad);
      form.target = iframeName;
      form.submit();

      setTimeout(() => settle(true), 4000);
    });
  }

  #showError(message) {
    if (!this.errorMessage) return;

    if (!message) {
      this.errorMessage.setAttribute('hidden', '');
      this.errorMessage.textContent = '';
      return;
    }

    this.errorMessage.textContent = message;
    this.errorMessage.removeAttribute('hidden');
  }

  #setLoadingState(isLoading) {
    if (!this.submitButton) return;

    this.submitButton.classList.toggle('loading', isLoading);
    this.submitButton.toggleAttribute('disabled', isLoading);
    this.submitButton.setAttribute('aria-disabled', String(isLoading));
  }
}

  customElements.define('byte-product-quote-request', ByteProductQuoteRequest);
}
